import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {DisplayedProduct, OverridePosition, Product, RelatedProductsProgram} from 'app/models/menu.model';
import {Organization} from "app/models/organization.model";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {TranslateService} from "@ngx-translate/core";
import {Store} from "@ngrx/store";
import {MenuService} from "app/services/menu.service";
import * as MenuSelectors from "app/reducers/menu.selectors";
import {RpProgramsService} from "app/services/rp-programs.service";
import {setRelatedProductsProgramDictionary} from "app/reducers/menu.actions";
import {
  EditRelatedProductsV3ModalComponent,
  EditRpProgramDataType
} from "app/pages/menu/shared/related-products-v3/edit-related-products-v3-modal/edit-related-products-v3-modal.component";

@UntilDestroy()
@Component({
  selector: 'app-related-products-v3',
  templateUrl: './related-products-v3.component.html',
  styleUrls: ['./related-products-v3.component.scss']
})
export class RelatedProductsV3Component implements OnInit {
  @Input() allowOverrides!: boolean;
  @Input() organization!: Organization;
  @Input() product!: DisplayedProduct;
  @Input() originalProduct!: Product;
  @Input() overridePosition!: OverridePosition;
  @Input() menuSectionId!: string;
  @Input() selectedMenuSections!: {};
  @Input() menuId: string;

  @Output() rpProgramIdsChange = new EventEmitter<string[]>();

  dictionaryRpPrograms: Map<string, RelatedProductsProgram>;
  originalDictionaryRpPrograms: Map<string, RelatedProductsProgram>;
  rpProgramIdsSet = new Set<string>();
  rpPrograms: RelatedProductsProgram[] = [];
  products: Map<string, Product> = new Map<string, Product>();

  constructor(
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<RelatedProductsV3Component>,
    private translate: TranslateService,
    private store: Store,
    private menuService: MenuService,
    private httpClient: RpProgramsService,
  ) {
  }

  ngOnInit(): void {
    this.store.select(MenuSelectors.selectRpPrograms)
      .pipe(untilDestroyed(this))
      .subscribe(rpPrograms => {
        this.rpPrograms = [...rpPrograms.values()];
        this.dictionaryRpPrograms = new Map<string, RelatedProductsProgram>();
        for (const kvp of rpPrograms) {
          this.dictionaryRpPrograms.set(kvp[0], kvp[1]);
        }
      });
    this.store.select(MenuSelectors.selectDictionaryProducts)
      .pipe(untilDestroyed(this))
      .subscribe(products => {
        this.products = products;
      });
    this.store.select(MenuSelectors.selectOriginalRpPrograms)
      .pipe(untilDestroyed(this))
      .subscribe(originalRpPrograms => {
        this.originalDictionaryRpPrograms = new Map<string, RelatedProductsProgram>();
        for (const kvp of originalRpPrograms) {
          this.originalDictionaryRpPrograms.set(kvp[0], kvp[1]);
        }
      });

    this.filRpProgramsIdSet(this.product.relatedProductsProgramIds)
  }

  isManuallyCreated(rpProgramId: string){
    return this.dictionaryRpPrograms.get(rpProgramId).isManuallyCreated && this.allowOverrides;
  }

  getName(rpProgramId: string){
    const rpProgram = this.dictionaryRpPrograms.get(rpProgramId);
    const name = rpProgram.name ?? "no name";
    const products = ` (products: ${rpProgram.products.length})`;

    return name + products;
  }

  async onDelete(rpProgramId: string){
    if(!this.allowOverrides){
      return;
    }
    const rpProgram = this.dictionaryRpPrograms.get(rpProgramId);
    if(this.rpProgramIdsSet.has(rpProgramId)
      && rpProgram.isManuallyCreated
      && confirm(`Are you sure you want to DELETE ${rpProgramId}`)){
      this.onRemove(rpProgramId);
      await this.httpClient.delete(rpProgramId);
      this.dictionaryRpPrograms.delete(rpProgramId);
      this.updateStoreStore();
    }
  }

  onRemove(rpProgramId: string){
    if(!this.allowOverrides){
      return;
    }
    if(this.rpProgramIdsSet.has(rpProgramId)){
      this.rpProgramIdsSet.delete(rpProgramId);

      this.rpProgramIdsChange.emit([...this.rpProgramIdsSet]);
    }
  }

  onPicked(rpProgramId: string){
    if(!this.allowOverrides){
      return;
    }

    this.rpProgramIdsSet.add(rpProgramId);
    this.rpProgramIdsChange.emit([...this.rpProgramIdsSet]);
  }

  onEdit(rpProgramId: string){
    if(!this.allowOverrides){
      return;
    }
    const rpProgram = this.dictionaryRpPrograms.get(rpProgramId) ?? {} as RelatedProductsProgram;
    const originalRpProgram = this.originalDictionaryRpPrograms.get(rpProgramId);
    this.dialog.open(EditRelatedProductsV3ModalComponent, {
      data: {
        id: rpProgramId,
        rpProgram: rpProgram,
        originalRpProgram: originalRpProgram,
        organization: this.organization,
        menuSectionId: this.menuSectionId,
        menuId: this.menuId,
        product: this.product,
        products: this.products,
      } as EditRpProgramDataType
    },).afterClosed().subscribe(result => {
      if(result.created){
        const created = result.created as RelatedProductsProgram;
        this.dictionaryRpPrograms.set(created.id, created)
        this.updateStoreStore();
        this.onPicked(created.id);
      } else if(result.updated){
        const updated = result.updated as RelatedProductsProgram;
        this.dictionaryRpPrograms.set(updated.id, updated);
        this.updateStoreStore();
        this.onPicked(updated.id);
      } else if(result.deleted){
        const deletedId = result.deleted as string;
        this.onRemove(deletedId);
        this.dictionaryRpPrograms.delete(deletedId);
        this.updateStoreStore();
      }
    });
  }

  restore(){
    this.rpProgramIdsSet.clear();
    this.filRpProgramsIdSet(this.originalProduct.relatedProductsProgramIds);
    this.rpProgramIdsChange.emit([...this.rpProgramIdsSet]);
  }

  canRestore(){
    return JSON.stringify([...this.rpProgramIdsSet].sort()) !== JSON.stringify([...this.originalProduct.relatedProductsProgramIds].sort());
  }

  filRpProgramsIdSet(ids: string[]){
    for (const rpProgramId of ids) {
      if(this.dictionaryRpPrograms.has(rpProgramId)){
        this.rpProgramIdsSet.add(rpProgramId);
      }
    }
  }

  private updateStoreStore() {
    this.store.dispatch(setRelatedProductsProgramDictionary(this.dictionaryRpPrograms))
  }
}
